import React, { Component, PropTypes } from 'react';
import accepts from 'attr-accept';
import { maxFileSize } from 'Constants';
import SteelCompass from 'services/SteelCompass';
import Message from 'services/Message';
import { RaisedButton } from 'material-ui';
import { Colors, Units, Shadow } from 'Css';
import classNames from 'classnames';

const css = oxygenCss({
  root: {
    position: 'absolute',
    left: Units.base * 2,
    right: Units.base * 2,
    top: Units.base * 2,
    bottom: Units.base * 2
  },
  heading: {
    fontSize: 24,
    lineHeight: '48px'
  },
  alternative: {
    lineHeight: '48px',
    textAlign: 'center'
  },
  description: {
    textAlign: 'center'
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '#ddd 4px dashed',
    borderRadius: '2px',
    cursor: 'pointer',
    lineHeight: '24px',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    ' input': {
      display: 'none'
    }
  },
  progressBar: {
    boxShadow: Shadow[1],
    position: 'relative',
    height: Units.base * 4,
    width: Units.keyline * 6,
    '@phone': {
      width: Units.keyline * 4
    }
  },
  progress: {
    lineHeight: `${Units.base * 4}px`,
    textAlign: 'center',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    color: Colors.text.Dark.secondary,
    backgroundColor: Colors.creen
  },
  input: {
    visibility: 'hidden'
  }
});

export default class FileUploader extends Component {
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
    onProgress: PropTypes.func,
    onSuccess: PropTypes.func,
    style: PropTypes.object,
    supportClick: PropTypes.bool,
    accept: PropTypes.string,
    multiple: PropTypes.bool,
    onDragLeave: PropTypes.func,
    description: PropTypes.node,
    onDragEnter: PropTypes.func
  };

  static contextTypes = {
    getIntlMessage: PropTypes.func
  };

  static defaultProps = {
    supportClick: true,
    multiple: true
  };

  state = {
    isDragActive: false,
    remaining: 0,
    progress: undefined
  };

  componentDidMount() {
    this.enterCounter = 0;
  }

  onDragLeave = event => {
    event.preventDefault();
    // Only deactivate once the dropzone and all children was left.
    if (--this.enterCounter > 0) {
      return;
    }
    const { onDragLeave } = this.props;

    this.setState({
      isDragActive: false,
      isDragReject: false
    });

    onDragLeave && onDragLeave();
  };

  onDragOver = event => {
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  onDragEnter = event => {
    const { onDragEnter } = this.props;
    event.preventDefault();
    const { dataTransfer } = event;
    dataTransfer.dropEffect = 'copy';
    ++this.enterCounter;

    const dataTransferItems =
      dataTransfer && dataTransfer.items ? dataTransfer.items : [];

    const allFilesAccepted = this.allFilesAccepted(
      Array.prototype.slice.call(dataTransferItems)
    );

    this.setState({
      isDragActive: allFilesAccepted,
      isDragReject: !allFilesAccepted
    });

    onDragEnter && onDragEnter();
  };

  onChange = event => {
    const { files } = event.target;
    this.processFiles(files);
  };

  onClick = event => {
    const { supportClick } = this.props;
    supportClick && this.open();
  };

  onDrop = event => {
    const { target, dataTransfer } = event;
    const { multiple } = this.props;
    event.preventDefault();

    this.setState({
      isDragActive: false
    });

    let files;
    if (dataTransfer) {
      files = dataTransfer.files;
    } else if (target) {
      files = event.target.files;
    }

    const maxFiles = multiple ? files.length : 1;
    for (let i = 0; i < maxFiles; i++) {
      files[i].preview = URL.createObjectURL(files[i]);
    }

    files = Array.prototype.slice.call(files, 0, maxFiles);
    this.processFiles(files);
  };

  open = () => {
    this._input.value = null;
    this._input.click();
  };

  handleTouchTap = event => {
    event.preventDefault();
    event.stopPropagation();
    this.open();
  };

  handleFile(files) {
    if (files.length < 1) {
      return;
    }
    const current = files.pop();
    const { onProgress, onSuccess } = this.props;
    if (current) {
      const fileType = current.type;
      const encoded =
        fileType.indexOf('audio') === 0 || fileType.indexOf('video') === 0;
      SteelCompass.upload([current], {
        encoded,
        progress: event => {
          onProgress &&
            event &&
            onProgress({
              ...event,
              remaining: files.length
            });
          this.setState({
            progress: event.percent,
            remaining: files.length
          });
        },
        error: () => {
          if (files.length > 0) {
            this.handleFile(files);
          }
        },
        success: response => {
          if (files.length > 0) {
            this.handleFile(files);
            this.setState(
              {
                progress: 0,
                remaining: files.length
              },
              () => {
                response &&
                  response.data &&
                  onSuccess &&
                  onSuccess(response.data);
              }
            );
          } else {
            this.setState(
              {
                progress: undefined,
                remaining: files.length
              },
              () => {
                response &&
                  response.data &&
                  onSuccess &&
                  onSuccess(response.data);
              }
            );
          }
        }
      });
    }
  }

  processFiles(files) {
    const fileList = Object.keys(files).map(key => files[key]);
    const willBeUploaded = fileList.filter(file => file.size < maxFileSize);
    const willBeDropped = fileList.filter(file => file.size >= maxFileSize);
    if (willBeDropped.length > 0) {
      if (willBeDropped.length > 1) {
        Message.info(willBeDropped.length + ' files are too big.');
      } else {
        Message.info(willBeDropped.length + ' file is too big.');
      }
    }
    if (willBeUploaded.length > 0) {
      this.handleFile(willBeUploaded);
    }
  }

  allFilesAccepted(files) {
    return files.every(file => accepts(file, this.props.accept));
  }

  render() {
    const {
      className,
      children,
      accept,
      style,
      description,
      ...other
    } = this.props;
    const { getIntlMessage } = this.context;
    const { isDragActive, remaining, progress } = this.state;
    const classes = classNames(className, css.wrapper);
    const styles = Object.assign(
      {
        backgroundColor: isDragActive ? Colors.material.yellow[50].hex : null
      },
      style
    );
    return (
      <div className={css.root}>
        <div
          className={classes}
          style={styles}
          onTouchTap={this.handleTouchTap}
          onDragLeave={this.onDragLeave}
          onDragOver={this.onDragOver}
          onDragEnter={this.onDragEnter}
          onDrop={this.onDrop}
          {...other}
        >
          {typeof progress === 'undefined' && (
            <div className={css.description}>
              <div className={css.heading}>{description}</div>
              <div className={css.alternative}>
                {getIntlMessage('module.orIfYouPrefer')}
              </div>
              <RaisedButton
                secondary
                label={getIntlMessage('module.selectAFileFromYourComputer')}
              />
            </div>
          )}
          {typeof progress !== 'undefined' && (
            <div className={css.alternative}>
              <div className={css.progressBar}>
                <div
                  className={css.progress}
                  style={{ width: `${progress}%` }}
                >{`${Math.round(progress)}%`}</div>
              </div>
              {remaining > 0 &&
                `${remaining} ${getIntlMessage('mediaLibrary.filesRemaining')}`}
            </div>
          )}
        </div>
        <input
          className={css.input}
          type="file"
          multiple={this.props.multiple}
          ref={_input => (this._input = _input)}
          onChange={this.onChange.bind(this)}
          accept={accept}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/components/MediaLibrary/FileUploader.js